import { contactWhatsAppUrl, empreendedoresPath, individualBookingUrl, siteCanonicalUrl } from '../siteDestinations'
import { clearAdsCookies, getAdsConsent, subscribeAdsConsent } from './consent'

export type AdsConfig = { enabled: boolean; id: string; agendaLabel: string; whatsappLabel: string }
export const adsConfig: AdsConfig = {
  enabled: import.meta.env.VITE_GOOGLE_ADS_ENABLED === 'true',
  id: import.meta.env.VITE_GOOGLE_ADS_ID ?? '',
  agendaLabel: import.meta.env.VITE_GOOGLE_ADS_GENERAL_AGENDA_LABEL ?? '',
  whatsappLabel: import.meta.env.VITE_GOOGLE_ADS_GENERAL_WHATSAPP_LABEL ?? '',
}
/** Rótulos próprios da página para empreendedores (as variáveis mantêm o nome da antiga campanha 50+). */
export const empreendedoresAdsConfig: AdsConfig = {
  ...adsConfig,
  agendaLabel: import.meta.env.VITE_GOOGLE_ADS_AGENDA_LABEL ?? '',
  whatsappLabel: import.meta.env.VITE_GOOGLE_ADS_WHATSAPP_LABEL ?? '',
}

export function adsConfigForPath(pathname: string) {
  return [empreendedoresPath, '/ia-para-empreendedores', '/ia-para-empreendedores/index.html'].includes(pathname)
    ? empreendedoresAdsConfig
    : adsConfig
}

type Gtag = (...args: unknown[]) => void
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

const denied = {
  ad_storage: 'denied', ad_user_data: 'denied',
  ad_personalization: 'denied', analytics_storage: 'denied',
}

export function canMeasureAds(config: AdsConfig, origin: string, pathname: string) {
  return config.enabled && origin === siteCanonicalUrl
    && /^\/(?:[a-z0-9-]+(?:\/|\/index\.html)?)?$/.test(pathname)
    && /^AW-\d+$/.test(config.id)
    && /^[\w-]+$/.test(config.agendaLabel) && /^[\w-]+$/.test(config.whatsappLabel)
}

/** Destinos conhecidos; nunca envia o texto do WhatsApp nem conteúdo do visitante. */
export function conversionForLink(href: string, config: AdsConfig): string | null {
  try {
    const url = new URL(href)
    if (url.href === individualBookingUrl) return `${config.id}/${config.agendaLabel}`
    const whatsapp = new URL(contactWhatsAppUrl)
    if (url.origin === whatsapp.origin && url.pathname === whatsapp.pathname) {
      return `${config.id}/${config.whatsappLabel}`
    }
  } catch { /* Links internos ou inválidos não são conversões. */ }
  return null
}

/** Preserva atribuição do anúncio sem repassar parâmetros livres da campanha. */
export function adsPageLocation(search: string, pathname = empreendedoresPath) {
  const page = new URL(pathname, siteCanonicalUrl)
  const params = new URLSearchParams(search)
  for (const name of ['gclid', 'dclid', 'gbraid', 'wbraid']) {
    const value = params.get(name)
    if (value && /^[A-Za-z0-9_.~-]{1,512}$/.test(value)) page.searchParams.set(name, value)
  }
  return page.href
}

/**
 * Consent Mode avançado: a tag carrega em toda visita na origem pública, com anúncio negado por padrão.
 * Sem aceite, o Google recebe só avisos sem cookies; o aceite libera cookies de atribuição.
 */
export function installAdsMeasurement(config = adsConfigForPath(window.location.pathname)) {
  if (!canMeasureAds(config, window.location.origin, window.location.pathname)) return () => {}
  let granted = false
  const sent = new Set<string>()
  const gtag: Gtag = function () {
    // gtag.js consome entradas semelhantes a arguments, não eventos arbitrários.
    window.dataLayer ??= []
    // eslint-disable-next-line prefer-rest-params -- gtag.js espera o objeto arguments do snippet oficial.
    window.dataLayer.push(arguments)
  }

  const updateConsent = () => {
    const accepted = getAdsConsent() === 'accepted'
    if (accepted === granted) return
    granted = accepted
    if (accepted) {
      gtag('consent', 'update', { ad_storage: 'granted', ad_user_data: 'granted' })
      return
    }
    gtag('consent', 'update', denied)
    clearAdsCookies()
  }
  const onClick = (event: MouseEvent) => {
    if (event.type === 'auxclick' && event.button !== 1) return
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null
    if (!(link instanceof HTMLAnchorElement)) return
    const conversion = conversionForLink(link.href, config)
    if (!conversion || sent.has(conversion)) return
    sent.add(conversion)
    // O valor fica definido na ação do Google Ads; enviar valor aqui o sobrescreveria.
    gtag('event', 'conversion', { send_to: conversion })
  }

  window.gtag = gtag
  gtag('consent', 'default', { ...denied, wait_for_update: 500 })
  updateConsent()
  gtag('set', 'ads_data_redaction', true)
  gtag('js', new Date())
  gtag('config', config.id, {
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    // Evita anexar parâmetros livres da URL ou da página anterior aos eventos.
    page_location: adsPageLocation(window.location.search, window.location.pathname),
    page_referrer: '',
  })
  if (!document.getElementById('oficina-google-ads')) {
    const script = document.createElement('script')
    script.id = 'oficina-google-ads'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.id}`
    document.head.append(script)
  }
  const unsubscribe = subscribeAdsConsent(updateConsent)
  document.addEventListener('click', onClick)
  document.addEventListener('auxclick', onClick)
  return () => {
    unsubscribe()
    document.removeEventListener('click', onClick)
    document.removeEventListener('auxclick', onClick)
  }
}
